import { loadPricingConfig } from './pricingConfigService';

/** Text priced for engraving — excludes builder-only connector reference labels. */
export function getEngravedCustomTextEntries(customText) {
  if (!Array.isArray(customText)) return [];
  return customText.filter((t) => {
    if (!t) return false;
    if (t.linkedConnectorId) return false;
    // Back-compat with older auto labels (id prefix + content pattern)
    if (typeof t.id === 'string' && t.id.startsWith('label_')) return false;
    const content = String(t.content || '');
    if (/^\s*#\d+\s*\|\s*/.test(content)) return false;
    return true;
  });
}

// Panel pricing configuration
const panelPricingConfig = {
  // Panel blank costs by material and size
  panelBlanks: {
    'flanged-aluminum': {
      '1U': 15.00,  // 19" x 1.75"
      '2U': 25.00,  // 19" x 3.5"
      '3U': 35.00,  // 19" x 5.25"
      '4U': 45.00   // 19" x 7"
    },
    'black-anodized': {
      '1G': 8.00,   // 4.5" x 4.5"
      '2G': 12.00,  // 4.5" x 4.5"
      '3G': 18.00,  // 6.75" x 4.5"
      '4G': 24.00   // 9" x 4.5"
    }
  },
  
  // Setup fees (CAD/Laser/CNC)
  setupFees: {
    'flanged-aluminum': 25.00,
    'black-anodized': 20.00
  },
  
  // Per-hole and etch pricing
  perHoleEtch: {
    'flanged-aluminum': 3.50,  // Per connector hole + etch
    'black-anodized': 3.00     // Per connector hole + etch
  },
  
  // Panel mount connector pricing (from existing pricing system)
  panelMountPricing: {
    // XLR 3-pin connectors
    'NC3FXX': { partCost: 3.50, laborCost: 2.50 },
    'NC3MXX': { partCost: 3.50, laborCost: 2.50 },
    'NC3FRX': { partCost: 4.25, laborCost: 2.50 },
    'NC3MRX': { partCost: 4.25, laborCost: 2.50 },
    'NC3FD-L-1': { partCost: 4.50, laborCost: 2.50 },
    'NC3MD-L-1': { partCost: 4.50, laborCost: 2.50 },
    'SC3F': { partCost: 3.25, laborCost: 2.50 },
    'SC3M': { partCost: 3.25, laborCost: 2.50 },
    'AC3F': { partCost: 3.00, laborCost: 2.50 },
    'AC3M': { partCost: 3.00, laborCost: 2.50 },
    'GEN-XLR-F': { partCost: 2.50, laborCost: 2.50 },
    'GEN-XLR-M': { partCost: 2.50, laborCost: 2.50 },
    
    // XLR 5-pin connectors
    'NC5FXX': { partCost: 4.25, laborCost: 2.50 },
    'NC5MXX': { partCost: 4.25, laborCost: 2.50 },
    'SC5F': { partCost: 4.00, laborCost: 2.50 },
    'SC5M': { partCost: 4.00, laborCost: 2.50 },
    
    // 1/4" connectors
    '2T_M': { partCost: 2.75, laborCost: 2.50 },
    '2T_F': { partCost: 2.75, laborCost: 2.50 },
    '2TR_M': { partCost: 3.00, laborCost: 2.50 },
    '2TR_F': { partCost: 3.00, laborCost: 2.50 },
    '2T_M_RA': { partCost: 3.25, laborCost: 2.50 },
    '2T_F_RA': { partCost: 3.25, laborCost: 2.50 },
    '2TR_M_RA': { partCost: 3.50, laborCost: 2.50 },
    '2TR_F_RA': { partCost: 3.50, laborCost: 2.50 },
    '2T_M_PM': { partCost: 3.50, laborCost: 2.50 },
    '2T_F_PM': { partCost: 3.50, laborCost: 2.50 },
    '2TR_M_PM': { partCost: 3.75, laborCost: 2.50 },
    '2TR_F_PM': { partCost: 3.75, laborCost: 2.50 },
    
    // RCA connectors
    'RCA_M': { partCost: 2.25, laborCost: 2.50 },
    'RCA_F': { partCost: 2.25, laborCost: 2.50 },
    'RCA_M_RA': { partCost: 2.50, laborCost: 2.50 },
    'RCA_F_RA': { partCost: 2.50, laborCost: 2.50 },
    'RCA_M_PM': { partCost: 2.75, laborCost: 2.50 },
    'RCA_F_PM': { partCost: 2.75, laborCost: 2.50 },
    
    // SpeakON connectors
    'NL2MP': { partCost: 3.25, laborCost: 2.50 },
    'NL2FP': { partCost: 3.25, laborCost: 2.50 },
    'NL8FP': { partCost: 4.50, laborCost: 2.50 },
    'NL4MPXX': { partCost: 4.00, laborCost: 2.50 },
    'NL8MPRXX': { partCost: 5.25, laborCost: 2.50 },
    
    // 3-pin connectors
    '3T_M': { partCost: 2.50, laborCost: 2.50 },
    '3T_F': { partCost: 2.50, laborCost: 2.50 },
    '3TR_M': { partCost: 2.75, laborCost: 2.50 },
    '3TR_F': { partCost: 2.75, laborCost: 2.50 },
    '3T_M_RA': { partCost: 2.75, laborCost: 2.50 },
    '3T_F_RA': { partCost: 2.75, laborCost: 2.50 },
    '3TR_M_RA': { partCost: 3.00, laborCost: 2.50 },
    '3TR_F_RA': { partCost: 3.00, laborCost: 2.50 },
    '3T_M_PM': { partCost: 3.00, laborCost: 2.50 },
    '3T_F_PM': { partCost: 3.00, laborCost: 2.50 },
    '3TR_M_PM': { partCost: 3.25, laborCost: 2.50 },
    '3TR_F_PM': { partCost: 3.25, laborCost: 2.50 },
    
    // Power connectors
    'NAC3MPXXA': { partCost: 8.50, laborCost: 3.00 },
    'NAC3MPXXB': { partCost: 8.50, laborCost: 3.00 },
    'NAC3FPX-TOP': { partCost: 9.00, laborCost: 3.00 },
    'NAC3MPX-TOP': { partCost: 9.00, laborCost: 3.00 },
    
    // IEC connectors
    'IEC_C14_panel_mount_male': { partCost: 4.50, laborCost: 2.50 },
    'IEC_C13_panel_mount_female': { partCost: 4.50, laborCost: 2.50 },
    'IEC_C14_cable_mount_male': { partCost: 3.75, laborCost: 2.50 },
    'IEC_C13_cable_mount_female': { partCost: 3.75, laborCost: 2.50 },
    
    // NEMA connectors
    'NEMA_5-15P_panel_mount_male': { partCost: 5.25, laborCost: 2.50 },
    'NEMA_5-15R_panel_mount_female': { partCost: 5.25, laborCost: 2.50 },
    'NEMA_5-15P_cable_mount_male': { partCost: 4.50, laborCost: 2.50 },
    'NEMA_5-15R_cable_mount_female': { partCost: 4.50, laborCost: 2.50 }
  },
  
  // Wire tailing pricing - use same system as cable builder
  // This will be loaded from the actual pricing config
  wireTailingPricing: {
    // Default fallback prices (will be overridden by actual pricing config)
    'PSM': 1.25,    // Professional Shielded Multi-Conductor Cable
    'PSI': 1.55,    // Professional Premium
    'Zip': 0.85,    // Zip Cable
    'Speaker': 0.95 // Speaker Cable
  },
  
  // Customization pricing
  customizationPricing: {
    customLogo: 10.00,     // Base logo engraving price
    customText: 0.00,      // No base cost per text item
    customTextPerChar: 0.20 // $0.20 per character
  },
  
  // Labor costs
  laborCosts: {
    panelAssembly: 2.50,    // Per connector
    wireTailing: 1.50,      // Per wire tail
    customization: 3.00     // Per customization item
  }
};

/**
 * Calculate the total price for a custom panel
 * @param {Object} panelConfig - Panel configuration object
 * @returns {Promise<number>} - Total price
 */
export async function calculatePanelPrice(panelConfig) {
  try {
    const { size, material, connectors, customLogo, customText, wireTailings } = panelConfig;
    
    // Load the same pricing config as cable builder
    const pricingConfig = await loadPricingConfig(false);
    const cableBasePrices = pricingConfig.cableBasePrices || {};
    const powerWireGaugePricing = pricingConfig.powerWireGaugePricing || {};
    const panelMountPricing = pricingConfig.panelMountPricing || {};
    const inlinePricing = pricingConfig.inlinePricing || {};
    
    // Determine panel size category based on dimensions
    let panelSize = 'custom';
    if (material === 'flanged-aluminum') {
      if (size.width === 19 && size.height === 1.75) panelSize = '1U';
      else if (size.width === 19 && size.height === 3.5) panelSize = '2U';
      else if (size.width === 19 && size.height === 5.25) panelSize = '3U';
      else if (size.width === 19 && size.height === 7) panelSize = '4U';
    } else if (material === 'black-anodized') {
      if (size.width === 4.5 && size.height === 4.5) panelSize = '1G';
      else if (size.width === 4.5 && size.height === 4.5) panelSize = '2G';
      else if (size.width === 6.75 && size.height === 4.5) panelSize = '3G';
      else if (size.width === 9 && size.height === 4.5) panelSize = '4G';
    }
    
    // Panel blank cost
    const panelBlankCost = panelPricingConfig.panelBlanks[material]?.[panelSize] || 
                          (size.width * size.height * 0.50); // Fallback for custom sizes
    
    // Setup fee
    const setupFee = panelPricingConfig.setupFees[material] || 25.00;
    
    // Per-hole and etch costs (one per connector)
    const perHoleEtchCost = panelPricingConfig.perHoleEtch[material] || 3.50;
    const totalHoleEtchCost = connectors.length * perHoleEtchCost;
    
    // Connector costs
    let connectorCost = 0;
    let connectorLaborCost = 0;
    
    for (const connector of connectors) {
      // Map connector type to pricing key
      const pricingKey = mapConnectorTypeToPricingKey(connector.connectorId || connector.type, {
        panelMountPricing,
        inlinePricing
      });
      const connectorPricing = panelMountPricing[pricingKey];
      
      if (connectorPricing) {
        // Add 20% mounting fee for proper panel mounting
        const mountingFee = connectorPricing.partCost * 0.20;
        connectorCost += connectorPricing.partCost + mountingFee;
        connectorLaborCost += connectorPricing.laborCost;
      } else {
        // Fallback pricing for unknown connectors with 20% mounting fee
        const baseCost = 3.00;
        const mountingFee = baseCost * 0.20;
        connectorCost += baseCost + mountingFee;
        connectorLaborCost += 2.50; // Default labor cost
        console.warn(`Unknown connector type: ${connector.type}, using fallback pricing with mounting fee`);
      }
    }
    
    // Wire tailing costs - use cable builder pricing for full connector pairs
    let wireTailingCost = 0;
    let wireTailingLaborCost = 0;
    
    for (const tailing of wireTailings) {
      if (tailing?.noTail) continue;
      const endAConn = tailing?.connectors?.endA?.connector;
      const endBConn = tailing?.connectors?.endB?.connector;
      const lengthInches = Number(tailing?.length);
      if (endAConn && endBConn && tailing.wireType && Number.isFinite(lengthInches) && lengthInches > 0) {
        // Use the same pricing logic as cable builder for wire tailings
        let basePrice = cableBasePrices.audio || 1.25; // Default to audio cable pricing
        
        // Map wire types to cable types for pricing
        if (tailing.wireType.startsWith('PSP')) {
          // Power cable - use power base price
          basePrice = cableBasePrices.power || 1.55;
          
          // Extract gauge from wire type (e.g., PSP12 -> 12AWG)
          const gauge = tailing.wireType.replace('PSP', '') + 'AWG';
          if (powerWireGaugePricing[gauge]) {
            basePrice = powerWireGaugePricing[gauge];
          }
        } else if (tailing.wireType.startsWith('PSA')) {
          // Audio cable - use audio base price
          basePrice = cableBasePrices.audio || 1.25;
        } else if (tailing.wireType === 'PSM') {
          // Multi-conductor - use audio base price
          basePrice = cableBasePrices.audio || 1.25;
        }
        
        // Length is stored in inches in PanelBuilder; pricing is per-foot.
        const lengthFeet = lengthInches / 12;
        const wireCost = basePrice * lengthFeet;
        
        // Add connector costs for both ends
        const endAPricing = panelMountPricing[mapConnectorTypeToPricingKey(endAConn, { panelMountPricing, inlinePricing })];
        // EndB is cable-mount: price from inlinePricing when available.
        const endBPricing =
          inlinePricing[mapConnectorTypeToPricingKey(endBConn, { panelMountPricing, inlinePricing })] ||
          panelMountPricing[mapConnectorTypeToPricingKey(endBConn, { panelMountPricing, inlinePricing })];
        
        let connectorCost = 0;
        let connectorLabor = 0;
        
        if (endAPricing) {
          connectorCost += endAPricing.partCost;
          connectorLabor += endAPricing.laborCost;
        }
        if (endBPricing) {
          connectorCost += endBPricing.partCost;
          connectorLabor += endBPricing.laborCost;
        }
        
        wireTailingCost += wireCost + connectorCost;
        wireTailingLaborCost += connectorLabor + panelPricingConfig.laborCosts.wireTailing;
      }
    }
    
    // Customization costs
    let customizationCost = 0;
    let customizationLaborCost = 0;
    
    if (customLogo) {
      // Calculate logo cost based on size multiplier
      const logoSizeMultiplier = customLogo.sizeMultiplier || 1.0;
      const logoCost = panelPricingConfig.customizationPricing.customLogo * logoSizeMultiplier;
      customizationCost += logoCost;
      customizationLaborCost += panelPricingConfig.laborCosts.customization;
    }
    
    const engravedTextEntries = getEngravedCustomTextEntries(customText);
    if (engravedTextEntries.length > 0) {
      for (const text of engravedTextEntries) {
        customizationCost += panelPricingConfig.customizationPricing.customText;
        // Add per-character cost for all text
        if (text.content && text.content.length > 0) {
          customizationCost += text.content.length * panelPricingConfig.customizationPricing.customTextPerChar;
        }
        customizationLaborCost += panelPricingConfig.laborCosts.customization;
      }
    }
    
    // Calculate total cost
    const totalCost = panelBlankCost + setupFee + totalHoleEtchCost + connectorCost + wireTailingCost + customizationCost;
    const totalLaborCost = connectorLaborCost + wireTailingLaborCost + customizationLaborCost;
    const totalPrice = totalCost + totalLaborCost;
    
    return Math.round(totalPrice * 100) / 100; // Round to 2 decimal places
    
  } catch (error) {
    console.error('❌ Error calculating panel price:', error);
    return 0;
  }
}

/**
 * Map connector type to pricing key
 * @param {string} connectorType - Connector type from UI
 * @returns {string} - Pricing key for lookup
 */
function mapConnectorTypeToPricingKey(connectorType, pricingTables = {}) {
  const panelMountPricing = pricingTables.panelMountPricing || panelPricingConfig.panelMountPricing || {};
  const inlinePricing = pricingTables.inlinePricing || {};

  // If a full connector id/sku key is provided and we have pricing for it, use it directly.
  if (connectorType && (panelMountPricing[connectorType] || inlinePricing[connectorType])) return connectorType;

  const typeMapping = {
    'XLR': 'NC3FXX', // Default to 3-pin XLR
    '1/4"': '2T_M',  // Default to 1/4" male
    'RCA': 'RCA_M',  // Default to RCA male
    'PowerCON': 'NAC3MPXXA', // Default to PowerCON panel mount
    'IEC': 'IEC_C14_panel_mount_male', // Default to IEC C14
    'NEMA': 'NEMA_5-15P_panel_mount_male' // Default to NEMA 5-15P
  };
  
  return typeMapping[connectorType] || 'NC3FXX'; // Fallback to XLR
}

/**
 * Get pricing breakdown for display
 * @param {Object} panelConfig - Panel configuration
 * @returns {Promise<Object>} - Detailed pricing breakdown
 */
export async function getPanelPricingBreakdown(panelConfig) {
  try {
    const { size, connectors, customLogo, customText, wireTailings } = panelConfig;

    // Pull the same cable pricing config used elsewhere so wire-tail costs reflect wire type + gauge.
    const pricingConfig = await loadPricingConfig(false);
    const cableBasePrices = pricingConfig.cableBasePrices || {};
    const powerWireGaugePricing = pricingConfig.powerWireGaugePricing || {};
    const panelMountPricing = pricingConfig.panelMountPricing || {};
    const inlinePricing = pricingConfig.inlinePricing || {};
    
    const area = size.width * size.height;
    let panelSizeCategory = 'custom';
    if (area < 20) panelSizeCategory = 'small';
    else if (area <= 50) panelSizeCategory = 'medium';
    else panelSizeCategory = 'large';
    
    const basePanelCost = panelPricingConfig.basePanelCosts[panelSizeCategory] * area;
    
    const breakdown = {
      basePanel: {
        cost: basePanelCost,
        description: `${size.width}" × ${size.height}" panel (${panelSizeCategory})`
      },
      connectors: {
        cost: 0,
        labor: 0,
        items: []
      },
      wireTailings: {
        cost: 0,
        labor: 0,
        items: []
      },
      customizations: {
        cost: 0,
        labor: 0,
        items: []
      }
    };
    
    // Connector breakdown
    for (const connector of connectors) {
      const pricingKey = mapConnectorTypeToPricingKey(connector.connectorId || connector.type, {
        panelMountPricing,
        inlinePricing
      });
      const connectorPricing = panelMountPricing[pricingKey];
      
      if (connectorPricing) {
        breakdown.connectors.cost += connectorPricing.partCost;
        breakdown.connectors.labor += connectorPricing.laborCost;
        breakdown.connectors.items.push({
          type: connector.type,
          partCost: connectorPricing.partCost,
          laborCost: connectorPricing.laborCost
        });
      }
    }
    
    // Wire tailing breakdown (PanelBuilder stores length in inches, wireType in PSP/PSM/etc)
    for (const tailing of wireTailings) {
      if (tailing?.noTail) continue;
      const endAConn = tailing?.connectors?.endA?.connector;
      const endBConn = tailing?.connectors?.endB?.connector;
      const lengthInches = Number(tailing?.length);
      if (!endAConn || !endBConn || !tailing?.wireType || !Number.isFinite(lengthInches) || lengthInches <= 0) continue;

      const lengthFeet = lengthInches / 12;
      const wirePricePerFoot = (() => {
        // Use the same logic as calculatePanelPrice: base price depends on wireType + gauge.
        if (tailing.wireType.startsWith('PSP')) {
          let base = cableBasePrices.power || 1.55;
          const gauge = tailing.wireType.replace('PSP', '') + 'AWG';
          if (powerWireGaugePricing[gauge]) base = powerWireGaugePricing[gauge];
          return base;
        }
        if (tailing.wireType.startsWith('PSA')) return cableBasePrices.audio || 1.25;
        if (tailing.wireType === 'PSM') return cableBasePrices.audio || 1.25;
        return cableBasePrices.audio || 1.25;
      })();

      const wireCost = wirePricePerFoot * lengthFeet;

      const endAPricing = panelMountPricing[mapConnectorTypeToPricingKey(endAConn, { panelMountPricing, inlinePricing })];
      const endBPricing =
        inlinePricing[mapConnectorTypeToPricingKey(endBConn, { panelMountPricing, inlinePricing })] ||
        panelMountPricing[mapConnectorTypeToPricingKey(endBConn, { panelMountPricing, inlinePricing })];
      const tailConnectorCost = (endAPricing?.partCost || 0) + (endBPricing?.partCost || 0);

      breakdown.wireTailings.cost += wireCost;
      breakdown.wireTailings.labor += panelPricingConfig.laborCosts.wireTailing;
      breakdown.wireTailings.items.push({
        wireType: tailing.wireType,
        lengthFeet: Math.round(lengthFeet * 10) / 10,
        wireCost: Math.round(wireCost * 100) / 100,
        tailConnectorsCost: Math.round(tailConnectorCost * 100) / 100
      });
    }
    
    // Customization breakdown
    if (customLogo) {
      // Calculate logo cost based on size multiplier
      const logoSizeMultiplier = customLogo.sizeMultiplier || 1.0;
      const logoCost = panelPricingConfig.customizationPricing.customLogo * logoSizeMultiplier;
      breakdown.customizations.cost += logoCost;
      breakdown.customizations.labor += panelPricingConfig.laborCosts.customization;
      breakdown.customizations.items.push({
        type: 'Custom Logo',
        sizeMultiplier: logoSizeMultiplier,
        cost: logoCost
      });
    }
    
    const engravedBreakdownText = getEngravedCustomTextEntries(customText);
    if (engravedBreakdownText.length > 0) {
      for (const text of engravedBreakdownText) {
        let textCost = panelPricingConfig.customizationPricing.customText;
        if (text.content && text.content.length > 0) {
          textCost += text.content.length * panelPricingConfig.customizationPricing.customTextPerChar;
        }
        breakdown.customizations.cost += textCost;
        breakdown.customizations.labor += panelPricingConfig.laborCosts.customization;
        breakdown.customizations.items.push({
          type: 'Custom Text',
          content: text.content,
          cost: textCost
        });
      }
    }
    
    const totalCost = breakdown.basePanel.cost + breakdown.connectors.cost + 
                     breakdown.wireTailings.cost + breakdown.customizations.cost;
    const totalLabor = breakdown.connectors.labor + breakdown.wireTailings.labor + 
                      breakdown.customizations.labor;
    const totalPrice = totalCost + totalLabor;
    
    return {
      ...breakdown,
      totals: {
        cost: totalCost,
        labor: totalLabor,
        total: totalPrice
      }
    };
    
  } catch (error) {
    console.error('❌ Error getting panel pricing breakdown:', error);
    return null;
  }
}

export default {
  calculatePanelPrice,
  getPanelPricingBreakdown,
  panelPricingConfig
};
